import { Injectable, effect, inject, signal } from '@angular/core';
import { forkJoin, of } from 'rxjs';
import { catchError, map } from 'rxjs/operators';
import { Asset, Transaction } from '../models/klidi.models';
import { KlidiApi } from './api.service';
import { MerchantContextService } from './merchant-context.service';

@Injectable({ providedIn: 'root' })
export class WalletService {
  private api = inject(KlidiApi);
  private context = inject(MerchantContextService);

  // Empty until the API answers. These were previously seeded with MOCK_ASSETS / a hardcoded
  // 15.86 balance / a hardcoded BTC price, which rendered for a beat on every load and then
  // snapped to the real figures — a wrong balance is worse than no balance, so views now show
  // a loading state instead (see `loading`).
  assets = signal<Asset[]>([]);
  transactions = signal<Transaction[]>([]);
  totalBalance = signal<number>(0);
  btcPrice = signal<number>(0);

  /** True from the moment a refresh starts until both calls settle. */
  loading = signal<boolean>(false);

  /** False until the first summary response lands, so "0.00" can be told apart from "not yet". */
  loaded = signal<boolean>(false);
  /**
   * Collapsed to the icon rail, i.e. the sidebar is not pinned. Restored from storage so the
   * choice survives a reload — a preference that resets on every visit is not a preference.
   */
  sidebarCollapsed = signal<boolean>(readStoredCollapsed());
  /** Mobile slide-in drawer state (only relevant under the responsive breakpoint). */
  mobileSidebarOpen = signal<boolean>(false);

  private lastRefreshedFor: number | null = null;

  constructor() {
    // Re-fetch whenever the active account changes — login, logout, or the user
    // switching between their personal account and a business account.
    //
    // allowSignalWrites is required: both branches write signals synchronously — clear() blanks
    // the figures, and refresh() raises `loading` before its first request. Without it Angular
    // throws NG0600 and the effect aborts before any request is made, so nothing ever loads.
    effect(() => {
      const id = this.context.activeMerchantId();
      if (id === null) {
        // No account to show (signed out, or Business mode with no business yet) —
        // blank the figures rather than leaving the previous account's balances up.
        this.lastRefreshedFor = null;
        this.clear();
        return;
      }
      if (id === this.lastRefreshedFor) return;
      this.lastRefreshedFor = id;
      this.refresh(id);
    }, { allowSignalWrites: true });
  }

  refresh(merchantId: number = this.context.activeMerchantId() ?? 0): void {
    if (!merchantId) return;

    // Two calls in flight; `pending` clears `loading` only once both have settled.
    this.loading.set(true);
    let pending = 2;
    const settle = () => { if (--pending === 0) this.loading.set(false); };

    this.api.walletSummary(merchantId).subscribe({
      next: s => {
        this.totalBalance.set(s.totalBalanceFiat);
        if (s.btcPrice > 0) this.btcPrice.set(s.btcPrice);
        this.assets.set(s.assets.map(a => ({
          id: a.id,
          symbol: a.symbol,
          name: a.name,
          balance: a.balance,
          balanceFiat: a.balanceFiat,
          currency: a.currency,
          network: a.network ?? undefined,
          icon: a.icon || a.symbol,
          color: a.color || '#7c3aed',
          change24h: a.change24h ?? undefined,
          type: a.type
        })));
        this.loaded.set(true);
        this.repriceCryptoIfUnconverted(merchantId);
        settle();
      },
      // Leave whatever is already on screen rather than blanking it — a failed reload should not
      // wipe a balance that was correct a moment ago. `loaded` stays false on a first-load failure,
      // so the view keeps showing its loading state instead of a confident 0.00.
      error: () => settle()
    });

    this.api.transactions(merchantId, 1, 50).subscribe({
      next: page => {
        this.transactions.set(page.items.map(t => ({
          id: t.id,
          type: t.type as Transaction['type'],
          amountPaid: t.amountPaid,
          amountPaidCurrency: t.amountPaidCurrency,
          networkFee: t.networkFee,
          processingFee: t.processingFee,
          netAmountReceived: t.netAmountReceived,
          netAmountCurrency: t.netAmountCurrency,
          date: new Date(t.date),
          status: (t.status as Transaction['status']) ?? 'Executed',
          receiptAvailable: t.receiptAvailable
        })));
        settle();
      },
      error: () => settle()
    });
  }

  /**
   * Value crypto holdings in EUR when the API hasn't.
   *
   * /wallet/summary used to return BalanceFiat as the raw ledger balance, so the total summed
   * different units — 12.64 EUR plus 0.042 ETH came out as 12.68 rather than counting the ETH at
   * its real worth. That is fixed in WalletController, but until it deploys the dashboard would
   * keep showing the wrong figure, so the balances are re-priced here.
   *
   * The trigger is exact equality between balance and balanceFiat on a held crypto asset, which
   * only happens when no conversion was applied. Once the API ships the fix this finds nothing and
   * costs no requests, and the whole method can be deleted.
   */
  private repriceCryptoIfUnconverted(merchantId: number): void {
    const unconverted = this.assets().filter(
      a => a.type === 'crypto' && a.balance > 0 && a.balanceFiat === a.balance
    );
    if (!unconverted.length) return;

    // sellRate prices the exact holding, so fiatAmount is what that balance is worth today. There
    // is no ticker endpoint on the UI's API surface, so this is the available pricing path.
    forkJoin(
      unconverted.map(a =>
        this.api.sellRate(merchantId, {
          fiatCurrency: 'EUR',
          fiatAmount: 0,
          cryptoCurrency: a.symbol,
          cryptoAmount: a.balance,
          ft: 1,
          type: 'wallet'
        }).pipe(
          map(q => ({ symbol: a.symbol, value: q.result ? q.fiatAmount : null })),
          // One unpriceable asset must not blank the others.
          catchError(() => of({ symbol: a.symbol, value: null }))
        )
      )
    ).subscribe(results => {
      const priced = new Map(
        results.filter(r => r.value !== null).map(r => [r.symbol, r.value as number])
      );
      if (!priced.size) return;

      this.assets.update(list =>
        list.map(a => priced.has(a.symbol) ? { ...a, balanceFiat: priced.get(a.symbol)! } : a)
      );
      this.totalBalance.set(this.assets().reduce((sum, a) => sum + a.balanceFiat, 0));
    });
  }

  /** Drop the current account's figures (kept out of refresh so a failed reload keeps its data). */
  clear(): void {
    this.assets.set([]);
    this.transactions.set([]);
    this.totalBalance.set(0);
    this.btcPrice.set(0);
    this.loaded.set(false);
  }

  getFiatAssets() { return this.assets().filter(a => a.type === 'fiat'); }
  getCryptoAssets() { return this.assets().filter(a => a.type === 'crypto'); }

  getExchangeRate(from: string, to: string): number {
    if (from === 'BTC' && to === 'EUR') return this.btcPrice();
    if (from === 'EUR' && to === 'BTC') return 1 / Math.max(this.btcPrice(), 1);
    return 1;
  }

  toggleSidebar() { this.setSidebarCollapsed(!this.sidebarCollapsed()); }

  /** Single writer for the state the collapse chevron and the Pin Sidebar toggle share. */
  setSidebarCollapsed(collapsed: boolean): void {
    this.sidebarCollapsed.set(collapsed);
    try {
      localStorage.setItem(SIDEBAR_COLLAPSED_KEY, collapsed ? '1' : '0');
    } catch {
      // Private browsing or a full quota: the sidebar still works, it just forgets.
    }
  }

  toggleMobileSidebar() { this.mobileSidebarOpen.update(v => !v); }
  closeMobileSidebar() { this.mobileSidebarOpen.set(false); }
}

const SIDEBAR_COLLAPSED_KEY = 'klidi.sidebarCollapsed';

/** Defaults to expanded (pinned), which is what a first-time visitor should see. */
function readStoredCollapsed(): boolean {
  try {
    return localStorage.getItem(SIDEBAR_COLLAPSED_KEY) === '1';
  } catch {
    return false;
  }
}
